import { ENDPIONTS, PaginatedResult, PagingOptions, httpService } from "@api";
import { ListAllowances, ListDeductions } from "@components";
import { Allowance, Deduction } from "@models";
import { ComplexHeader, Table } from "@shared";
import { AllowanceStatus, DeductionsRef, PayrollPreviewVM, RecurringDeductionsRef } from "@viewModels";
import React, { useEffect, useRef, useState } from "react";

type toBeUpdatedFields = {
  allowances: number;
  deductions: number;
  deductionsRef: DeductionsRef[];
  empDeductionsRef: PaginatedResult<Deduction>;
};

interface Props {
  payrollPreview: PayrollPreviewVM;
  callback?: (
    allowances: number,
    deductions: number,
    deductionsRef: DeductionsRef[],
    empDeductionsRef: PaginatedResult<Deduction>
  ) => void;
  employeeDeductions?: {
    [key: string]: PaginatedResult<Deduction>;
  };
}

const ModifyEmployeePayroll: React.FC<Props> = ({ callback, payrollPreview, employeeDeductions }) => {
  const [validating, setValidating] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const modifications = useRef<toBeUpdatedFields>({
    allowances: payrollPreview.allowances,
    deductions: payrollPreview?.deductionRefs.reduce((prev, curr) => {
      return prev + curr.monthlyInstallment;
    }, 0),
    deductionsRef: payrollPreview.deductionRefs,
    empDeductionsRef: new PaginatedResult<Deduction>(),
  });

  const updatedDeductions = useRef<Deduction[]>([]);

  const updateEmpData = async () => {
    if (updatedDeductions.current.length === 0) return;
    setValidating(true);
    setErrMsg(null);

    // First: check if the new deductions made are feasible.
    var obj = {
      empId: payrollPreview.id,
      deductions: updatedDeductions.current?.map((d) => ({
        deductionId: d.id,
        amount: modifications.current.deductionsRef.find((dref) => dref.id === d.id)?.monthlyInstallment ?? d.amount,
      })),
    };

    var res = await httpService(ENDPIONTS.checkEmpDeductionFeasibility).post(obj);
    if (res?.status === 200 && res?.data?.status === true) {
      // if the new deductions are feasible, proceed.
      let temp = new PaginatedResult<Deduction>();
      temp.items = [...updatedDeductions.current];

      modifications.current.empDeductionsRef = temp;
      callback?.(
        modifications.current.allowances,
        modifications.current.deductions,
        modifications.current.deductionsRef,
        modifications.current.empDeductionsRef
      );
    } else {
      setErrMsg(res?.data?.message);
    }

    setValidating(false);
  };

  const modifyDeductionsRef = (values: Record<string, any>, deductions: Deduction[]) => {
    var newDeductionsRef: DeductionsRef[] = [];
    Object.keys(values).map((k) => {
      return newDeductionsRef.push({ id: +k, monthlyInstallment: +values[k] });
    });
    modifications.current.deductions = newDeductionsRef.reduce((prev, curr) => {
      return prev + curr.monthlyInstallment;
    }, 0);
    modifications.current.deductionsRef = newDeductionsRef;

    modifications.current = { ...modifications.current };
    updatedDeductions.current = [...deductions];

    // Update the total deductions in the input field
    var recurringDeductions = payrollPreview?.recurringDeductionsRef.reduce((prev, curr) => {
      return prev + curr.amount;
    }, 0);

    var totalDeductions = recurringDeductions + modifications.current?.deductions;
    (document.querySelector("#totalDeductions") as HTMLInputElement).value =totalDeductions.toCurrency();
  };

  const addDeductionsOptions = (): PagingOptions => {
    var options = new PagingOptions();
    options.filter<Deduction>((f) => f.gt("ballance", 0));
    return options;
  };

  const addAllowancessOptions = (): PagingOptions => {
    var options = new PagingOptions();
    options.filter<Allowance>((f) => f.eq("status", AllowanceStatus.Ongoing));
    return options;
  };

  const recurringHeaders: ComplexHeader[] = [
    { key: "type", title: "Type" },
    {
      key: "value",
      title: "value",
    },
    { key: "amount", title: "amount", format: "currency", total: { format: "currency" } },
  ];

  return (
    <>
      <h4>Employee Payroll Details - {payrollPreview.name}</h4>
      <hr />
      <div className="row">
        <h4>Employee Allowances</h4>
        <ListAllowances
          employeeId={payrollPreview.id}
          showInPopUp
          options={addAllowancessOptions()}
          showActionButtons={false}
        />
      </div>
      <div className="row mt-5">
        <h4>Employee Recurring Deductions</h4>
        <Table
          class="table table-striped table-hover mb-0"
          data={payrollPreview.recurringDeductionsRef ?? []}
          headers={recurringHeaders}
          showCounter
          showTotals
        />
      </div>
      <div className="row mt-5">
        <h4>Employee Loans</h4>
        <ListDeductions
          employeeId={payrollPreview.id}
          showInPopUp
          hideInputsInTable={false}
          callback={modifyDeductionsRef}
          options={addDeductionsOptions()}
          showActionButtons={false}
          employeeDeductions={employeeDeductions}
        />
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <label>Total Allowances</label>
            <div className="col-12">
              <input
                type="text"
                disabled
                value={modifications.current?.allowances?.toCurrency()}
                className="form-control"
                // onChange={(e) =>
                //   setModification((prev) => ({
                //     ...prev,
                //     allowances: +e.target.value,
                //   }))
                // }
              />
            </div>
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label>Total Deductions</label>
            <div className="col-12">
              <input
                disabled
                type="text"
                id="totalDeductions"
                value={(
                  payrollPreview?.recurringDeductionsRef.reduce((prev, curr) => {
                    return prev + curr.amount;
                  }, 0) + modifications.current?.deductions
                )?.toCurrency()}
                className="form-control"
                // onChange={(e) => setModification(prev => ({ ...prev, deductions: +e.target.value }))}
              />
            </div>
          </div>
        </div>
        {payrollPreview.employeeCategory !== "Others" && (
          <>
            <div className="col">
              <div className="form-group">
                <label>Absence Days</label>
                <div className="col-12">
                  <input
                    type="text"
                    disabled
                    value={payrollPreview.absenceDays ?? 0}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col">
              <div className="form-group">
                <label>Absence Deduction</label>
                <div className="col-12">
                  <input
                    type="text"
                    disabled
                    value={(payrollPreview.absenceAmount ?? 0).toCurrency()}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </>
        )}
        <div className="col-3 mt-1 pt-1">
          <input
            type="button"
            className="btn btn-dark mt-3"
            value={validating ? "Validating..." : "Modify"}
            onClick={updateEmpData}
          />
        </div>
      </div>
      {errMsg && (
        <div className="d-flex justify-content-start">
          <span className="text-danger">{errMsg}</span>
        </div>
      )}
    </>
  );
};

export default ModifyEmployeePayroll;
